import React, { useCallback } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { ContentTableGrid } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentTableGrid';
import { ContentDropdownBarChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentBarChart';
import { ContentSplineChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentSplineChart';
import ContentKeyMetricsSpiderChart from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentKeyMetricsSpiderChart';
import { useContentService } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useContentService';
import {
  CategoryType,
  ContentMetricType
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import { useMainEntityType, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { KeyMetricsFilterAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/KeyMetricsAdapter';
import { BEACON_TABS, BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { LabelClickFunction } from 'src/components/BeaconRedesignComponents/SpiderChart/SpiderChart';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import {
  BarChartData,
  KeywordFilter
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import { formatMetric } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useContentChartSubtitles } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useDynamicSubtitle';
import { getWeekLastDate } from 'src/utils/dateformatting';
import {
  createMetricColumnDefinition,
  createWeekEndingColumn,
  creteProductColumWithSetWidth
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/utils';
import colors from 'src/utils/colors';

const ContentSummaryPage = () => {
  const {
    getContentScoresKeyMetrics,
    getContentScoresByWeek,
    getContentScoresByProduct,
    getContentScoreByCategory,
    getContentScoresKeyMetricsByWeek
  } = useContentService();

  const {
    generateSplineChartSubtitle,
    generateBarChartSubtitle,
    generateTableGridSubtitle,
    generateSpiderChartSubtitle
  } = useContentChartSubtitles();

  const title = 'Content Score';
  const entityType = useMainEntityType();
  const { beaconNavigator } = useBeaconNavigator();
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();

  const groupByOptionsByEntityType = {
    default: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentScore, CategoryType.Brand)
      }
    ],
    brand: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentScore, CategoryType.Subcategory)
      }
    ],
    category: [
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentScore, CategoryType.Brand)
      }
    ],
    subcategory: [
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentScore, CategoryType.Brand)
      }
    ]
  };

  /**
   * Handles the click event on the spider chart labels
   */
  const handleLabelClick: LabelClickFunction = useCallback(
    ({ labelData }) => {
      const { name: labelName } = labelData as {
        id: string;
        name: string;
        value: number;
        change: number;
      };

      // Get the current URL search parameters
      const currentUrl = new URL(window.location.href);
      const { searchParams } = currentUrl;

      // Create a new navigator instance
      const navigator = beaconNavigator;

      // Clear existing navigation parameters
      navigator.clearSearchParams();

      // Add back all the existing parameters except navigation ones
      searchParams.forEach((value, key) => {
        if (key !== 'tab' && key !== 'subtab' && key !== 'keywordFilter') {
          navigator.addSearchParams({ [key]: value });
        }
      });

      switch (labelName) {
        case 'Content Utilization':
          navigator.addSearchParams({
            tab: BEACON_TABS.CONTENT_V2,
            subtab: BEACON_SUBTABS.CONTENT_UTILIZATION
          });
          break;
        case 'Content Accuracy':
          navigator.addSearchParams({
            tab: BEACON_TABS.CONTENT_V2,
            subtab: BEACON_SUBTABS.CONTENT_ACCURACY_V2
          });
          break;
        case 'Voice Match Score':
        case 'Tone Match Score':
          navigator.addSearchParams({
            tab: BEACON_TABS.CONTENT_V2,
            subtab: BEACON_SUBTABS.CONTENT_VOICE_AND_TONE
          });
          break;
        case 'Keyword Usage':
          navigator.addSearchParams({
            tab: BEACON_TABS.CONTENT_V2,
            subtab: BEACON_SUBTABS.CONTENT_KEYWORDS,
            keywordFilter: KeywordFilter.Usage
          });
          break;
        case 'Keyword Productivity':
          navigator.addSearchParams({
            tab: BEACON_TABS.CONTENT_V2,
            subtab: BEACON_SUBTABS.CONTENT_KEYWORDS,
            keywordFilter: KeywordFilter.Productivity
          });
          break;
        case 'Keyword Popularity':
          navigator.addSearchParams({
            tab: BEACON_TABS.CONTENT_V2,
            subtab: BEACON_SUBTABS.CONTENT_KEYWORDS,
            keywordFilter: KeywordFilter.Popularity
          });
          break;
        default:
          return;
      }

      navigator.go();
    },
    [beaconNavigator]
  );

  // Non-breaking space, equivalent to &nbsp;
  const nbsp = String.fromCharCode(160);

  return (
    <BasePageContainer sx={{ display: 'flex', flexDirection: 'column', gap: `${BEACON_CHART_SPACING}px` }}>
      <ContentKeyMetricsSpiderChart
        type={ContentMetricType.ContentScore}
        title="Key Metrics"
        renderSubtitle={(score) => {
          return generateSpiderChartSubtitle({
            formattedValue: score,
            metricName: 'overall content score'
          });
        }}
        spiderChartProps={{
          handleLabelClick,
          labelStyles: {
            0: {
              transform: 'translate(-82px, -45px)',
              alignItems: 'center',
              position: 'absolute'
            },
            1: {
              transform: 'translate(45.5px, -58px)',
              position: 'absolute'
            },
            2: {
              transform: 'translate(-4.5px, -22px)',
              position: 'absolute'
            },
            3: {
              transform: 'translate(-42px, 6.7px)',
              position: 'absolute'
            },
            4: {
              transform: 'translate(-115px, 6.7px)',
              position: 'absolute'
            },
            5: {
              transform: 'translate(-131.3px, -21px)',
              position: 'absolute'
            },
            6: {
              transform: 'translate(-176.3px, -52px)',
              position: 'absolute'
            }
          }
        }}
        queryFn={async () =>
          new KeyMetricsFilterAdapter(
            await getContentScoresKeyMetrics(ContentMetricType.ContentScore, mainTimePeriod, comparisonTimePeriod),
            {
              contentUtilizationScore: 'Content Utilization',
              contentAccuracyScore: 'Content Accuracy',
              keywordUsageScore: 'Keyword Usage',
              keywordProductivityScore: 'Keyword Productivity',
              keywordPopularityScore: 'Keyword Popularity',
              voiceMatchScore: 'Voice Match Score',
              toneMatchScore: 'Tone Match Score'
            }
          )
        }
      />
      <div style={{ marginTop: '80px' }}>
        <ContentSplineChart
          title={title}
          queryFn={() => getContentScoresByWeek(ContentMetricType.ContentScore, mainTimePeriod, comparisonTimePeriod)}
          renderSubtitle={(score: string, change: number) => {
            return generateSplineChartSubtitle({
              formattedAverage: score,
              metricName: 'overall content score',
              formattedPercentChange: formatMetric(Math.abs(change), METRICTYPE.PERCENT, { decimalPlaces: 2 }),
              direction: change > 0 ? 'higher' : 'lower'
            });
          }}
        />
      </div>
      {entityType !== 'product' && (
        <ContentDropdownBarChart
          prefix={`${title} by`}
          options={groupByOptionsByEntityType[entityType] || groupByOptionsByEntityType.default}
          renderSubtitle={(selectedOption, data: BarChartData) => {
            const count = data.chartData[0].categories.length;

            const groupByText = (() => {
              switch (selectedOption) {
                case 'category':
                  return count === 1 ? 'from 1 category' : `from all ${count} categories`;
                case 'subcategory':
                  return count === 1 ? 'from 1 subcategory' : `from all ${count} subcategories`;
                case 'brand':
                  return count === 1 ? 'from 1 brand' : `from all ${count} brands`;
                default:
                  return '';
              }
            })();

            return generateBarChartSubtitle({
              metricName: 'overall content score',
              groupByText
            });
          }}
        />
      )}

      {entityType !== 'product' && (
        <ContentTableGrid
          title={`${title} by Product`}
          productColumn={creteProductColumWithSetWidth('247px')}
          columnDefinitions={[
            createMetricColumnDefinition(['contentScore', 'Content Score'], { cellStyle: { width: '62px' } }),
            createMetricColumnDefinition(['contentUtilizationScore', 'Content Utilization'], {
              cellStyle: { width: '84px' }
            }),
            createMetricColumnDefinition(['contentAccuracyScore', 'Content Accuracy'], {
              cellStyle: { width: '71px' }
            }),
            createMetricColumnDefinition(['keywordUsageScore', 'Keyword Usage'], { cellStyle: { width: '64px' } }),
            createMetricColumnDefinition(['keywordProductivityScore', 'Keyword Productivity'], {
              cellStyle: { width: '98px' }
            }),
            createMetricColumnDefinition(['keywordPopularityScore', 'Keyword Popularity'], {
              cellStyle: { width: '84px' }
            }),
            createMetricColumnDefinition(['voiceMatchScore', `Voice${nbsp}Match Score`], {
              cellStyle: { width: '91px' }
            }),
            createMetricColumnDefinition(['toneMatchScore', `Tone${nbsp}Match Score`], { cellStyle: { width: '87px' } })
          ]}
          queryFn={() =>
            getContentScoresByProduct(ContentMetricType.ContentScore, mainTimePeriod, comparisonTimePeriod)
          }
          renderSubtitle={(view) => {
            return generateTableGridSubtitle({
              view,
              metricName: 'overall content score'
            });
          }}
        />
      )}

      {entityType === 'product' && (
        <ContentTableGrid
          title="Content Score by Week"
          columnDefinitions={[
            createWeekEndingColumn(),
            ...[
              createMetricColumnDefinition(['contentScore', 'Content Score'], { cellStyle: { width: '62px' } }),
              createMetricColumnDefinition(['contentUtilizationScore', 'Content Utilization'], {
                cellStyle: { width: '84px' },
                showPercentChange: false
              }),
              createMetricColumnDefinition(['contentAccuracyScore', 'Content Accuracy'], {
                cellStyle: { width: '71px' },
                showPercentChange: false
              }),
              createMetricColumnDefinition(['keywordUsageScore', 'Keyword Usage'], {
                cellStyle: { width: '64px' },
                showPercentChange: false
              }),
              createMetricColumnDefinition(['keywordProductivityScore', 'Keyword Productivity'], {
                cellStyle: { width: '98px' },
                showPercentChange: false
              }),
              createMetricColumnDefinition(['keywordPopularityScore', 'Keyword Popularity'], {
                cellStyle: { width: '84px' },
                showPercentChange: false
              }),
              createMetricColumnDefinition(['voiceMatchScore', `Voice${nbsp}Match Score`], {
                cellStyle: { width: '91px' },
                showPercentChange: false
              }),
              createMetricColumnDefinition(['toneMatchScore', `Tone${nbsp}Match Score`], {
                cellStyle: { width: '87px' },
                showPercentChange: false
              })
            ]
          ]}
          tableSx={{
            minWidth: 650,
            borderCollapse: 'unset',
            '& tbody .MuiTableCell-root': {
              padding: '12px 32px',
              height: '60px',
              paddingRight: '0px'
            },
            '& thead .MuiTableCell-root': {
              padding: '0px 0px 16px 32px'
            },
            '& tbody td': {
              borderBottom: '1px solid #f0f0f0'
            },
            '& thead th': {
              border: 'none'
            },
            '& thead th:first-of-type': {
              padding: '0px 0px 16px 16px'
            },
            '& tbody td:first-of-type': {
              borderLeft: '1px solid #f0f0f0',
              padding: '16px 0px 16px 16px'
            },
            '& tbody td:last-child': {
              borderRight: '1px solid #f0f0f0'
            },
            '& tbody tr:first-of-type td': {
              borderTop: '1px solid #f0f0f0'
            },
            '& tbody tr:first-of-type td:first-of-type': {
              borderTopLeftRadius: '8px'
            },
            '& tbody tr:first-of-type td:last-of-type': {
              borderTopRightRadius: '8px'
            },
            '& tbody tr:first-of-type td:last-child': {
              borderTopRightRadius: '8px'
            },
            '& tbody tr:last-child td:first-of-type': {
              borderBottomLeftRadius: '8px'
            },
            '& tbody tr:last-child td:last-child': {
              borderBottomRightRadius: '8px'
            },
            '& thead .MuiTableCell-root span': {
              fontFamily: 'Roboto',
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: '20px',
              letterSpacing: '1.2px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              color: colors.darkBlue,
              textTransform: 'uppercase'
            }
          }}
          mode="table"
          queryFn={() => getContentScoresKeyMetricsByWeek(mainTimePeriod, comparisonTimePeriod)}
          renderSubtitle={(_, data) => {
            const latestWeek = data[0] as unknown as {
              weekId: string;
              contentScore: { value: number; difference: number };
            };

            const formattedDate = getWeekLastDate(latestWeek?.weekId).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            });
            const formattedValue = formatMetric(Math.abs(latestWeek?.contentScore.value), METRICTYPE.PERCENT, {
              decimalPlaces: 2
            });
            const formattedDifference = formatMetric(Math.abs(latestWeek?.contentScore.difference), METRICTYPE.PERCENT, {
              decimalPlaces: 2
            });
            const direction = latestWeek?.contentScore.difference > 0 ? 'higher' : 'lower';
            return `This product's overall content score for week ending ${formattedDate} is ${formattedValue}, which is ${formattedDifference} ${direction} compared to the prior year for the same week.`;
          }}
        />
      )}
    </BasePageContainer>
  );
};

export default ContentSummaryPage;
